<template>
  <Dialog
    id="notify-client-dialog"
    :visible.sync="localShow"
    :style="{ width: $isMobile() ? '100vw' : '92vw', maxWidth: '720px' }"
    header=""
    :modal="true"
    :closable="false"
    class="notify-dialog"
    @hide="onHide"
  >
    <Loading :active.sync="loading" :isFullPage="false" :zIndex="9000" />

    <div class="notify-header">
      <div class="notify-header__left">
        <i class="pi pi-send notify-header__icon"></i>
        <div>
          <h2 class="notify-header__title">Notificar Cliente</h2>
          <p class="notify-header__subtitle">PAT #{{ patId }}</p>
        </div>
      </div>
      <button class="notify-header__close" @click="close">
        <i class="pi pi-times"></i>
      </button>
    </div>

    <div class="notify-body">
      <div class="share-panel">
        <div class="share-panel__header">
          <i class="pi pi-pencil"></i>
          <span>Mensagem</span>
        </div>

        <div class="share-row share-row--subject">
          <div class="share-row__label">
            <i class="pi pi-tag"></i>
            <span>Assunto</span>
          </div>
          <div class="share-row__input">
            <InputText
              v-model="data.subject"
              placeholder="Assunto da notificação"
              class="share-input"
            />
          </div>
        </div>

        <div class="share-row share-row--message">
          <Textarea
            v-model="data.message"
            :placeholder="messagePlaceholder"
            :autoResize="true"
            rows="4"
            :maxlength="SMS_MAX_LENGTH"
            class="share-input share-input--textarea"
          />
        </div>
        <div class="share-row share-row--counter">
          <small :class="['char-counter', { 'char-counter--warn': remainingChars <= 20 }]">
            {{ (data.message || '').length }} / {{ SMS_MAX_LENGTH }} caracteres
          </small>
        </div>

        <div class="share-row">
          <div class="share-row__label">
            <i class="pi pi-envelope"></i>
            <span>E-mail</span>
          </div>
          <div class="share-row__input">
            <InputText
              v-model="data.email"
              placeholder="email@exemplo.com"
              class="share-input"
            />
          </div>
          <Button
            label="Enviar"
            icon="pi pi-send"
            class="p-button-sm share-btn share-btn--email"
            :disabled="!canSendEmail"
            :loading="sendingEmail"
            @click="sendEmail"
          />
        </div>

        <div class="share-row">
          <div class="share-row__label">
            <i class="pi pi-comment"></i>
            <span>SMS</span>
          </div>
          <div class="share-row__input">
            <InputText
              v-model="data.phone"
              placeholder="9XX XXX XXX"
              class="share-input share-input--phone"
            />
          </div>
          <Button
            label="Enviar"
            icon="pi pi-comment"
            class="p-button-sm share-btn share-btn--sms"
            :disabled="!canSendSms"
            :loading="sendingSms"
            @click="sendSms"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="notify-footer">
        <Button
          label="Fechar"
          icon="pi pi-times"
          class="p-button-secondary"
          @click="close"
        />
      </div>
    </template>
  </Dialog>
</template>

<script>
import PatService from "@/services/pat.service";

export default {
  name: "NotifyClientDialog",
  props: {
    show: { type: Boolean, default: false },
    patId: { type: [Number, String], required: true },
  },
  data() {
    return {
      localShow: false,
      loading: false,
      sendingEmail: false,
      sendingSms: false,
      SMS_MAX_LENGTH: 160,
      data: {
        email: "",
        phone: "",
        subject: "",
        message: "",
      },
    };
  },
  computed: {
    canSendEmail() {
      return !!this.data.email && !!this.data.subject && !!this.data.message && !this.sendingEmail;
    },
    canSendSms() {
      return !!this.data.phone && !!this.data.message && !this.sendingSms;
    },
    remainingChars() {
      return this.SMS_MAX_LENGTH - (this.data.message || "").length;
    },
    messagePlaceholder() {
      return `Escreva a mensagem (máx. ${this.SMS_MAX_LENGTH} caracteres para SMS)...`;
    },
  },
  watch: {
    show(val) {
      this.localShow = val;
      if (val) {
        this.resetData();
        this.loadClientInfo();
      }
    },
    localShow(val) {
      if (!val && this.show) {
        this.$emit("close");
      }
    },
  },
  methods: {
    resetData() {
      this.data = { email: "", phone: "", subject: "", message: "" };
    },
    async loadClientInfo() {
      this.loading = true;
      try {
        const info = await PatService.getNotifyClientInfo(this.patId);
        if (info) {
          this.data.email = info.email || "";
          this.data.phone = info.phone || "";
        }
      } catch {
        this.$toast.add({
          severity: "warn",
          summary: "Aviso",
          detail: "Não foi possível obter os contactos do cliente.",
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    async sendEmail() {
      if (!this.canSendEmail) return;
      this.sendingEmail = true;
      try {
        await PatService.sendNotifyClientEmail(this.patId, {
          email: this.data.email,
          subject: this.data.subject,
          message: this.data.message,
        });
        this.$toast.add({
          severity: "success",
          summary: "E-mail enviado",
          detail: "Notificação enviada com sucesso.",
          life: 3000,
        });
      } catch {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Não foi possível enviar o e-mail.",
          life: 3000,
        });
      } finally {
        this.sendingEmail = false;
      }
    },
    async sendSms() {
      if (!this.canSendSms) return;
      this.sendingSms = true;
      try {
        await PatService.sendNotifyClientSms(this.patId, {
          phone: this.data.phone,
          subject: this.data.subject,
          message: this.data.message,
        });
        this.$toast.add({
          severity: "success",
          summary: "SMS enviado",
          detail: "Notificação enviada com sucesso.",
          life: 3000,
        });
      } catch {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Não foi possível enviar o SMS.",
          life: 3000,
        });
      } finally {
        this.sendingSms = false;
      }
    },
    close() {
      this.localShow = false;
      this.$emit("close");
    },
    onHide() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
:deep(.notify-dialog .p-dialog-header) { display: none; }
:deep(.notify-dialog .p-dialog-content) { padding: 0; border-radius: 10px 10px 0 0; }
:deep(.notify-dialog .p-dialog-footer) { padding: 0; border-radius: 0 0 10px 10px; }

.notify-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: #fff;
  border-radius: 10px 10px 0 0;
}
.notify-header__left { display: flex; align-items: center; gap: 0.875rem; }
.notify-header__icon { font-size: 1.5rem; color: #2563eb; }
.notify-header__title { margin: 0; font-size: 1.05rem; font-weight: 700; color: #1a1a2e; }
.notify-header__subtitle { margin: 0; font-size: 0.8rem; color: #6c757d; }
.notify-header__close {
  background: none; border: none; cursor: pointer;
  width: 36px; height: 36px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #6c757d; transition: background 0.2s, color 0.2s;
}
.notify-header__close:hover { background: #f1f3f5; color: #1a1a2e; }

.notify-body { background: #f8f9fa; }

.share-panel {
  background: #fff;
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.share-panel__header {
  display: flex; align-items: center; gap: 0.5rem;
  font-size: 0.8rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.05em; color: #6c757d;
}
.share-row { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.share-row--message { align-items: stretch; }
.share-row__label {
  display: flex; align-items: center; gap: 0.4rem;
  min-width: 72px; font-size: 0.875rem; font-weight: 600; color: #495057;
}
.share-row__input { flex: 1; min-width: 180px; }
.share-input { width: 100% !important; }
:deep(.share-input .p-inputtext),
:deep(.share-input.p-inputtext) { font-size: 0.875rem; }
.share-btn { white-space: nowrap; }
.share-btn--email { background: #2563eb !important; border-color: #2563eb !important; }
.share-btn--email:hover:not(:disabled) { background: #1d4ed8 !important; border-color: #1d4ed8 !important; }
.share-btn--sms { background: #16a34a !important; border-color: #16a34a !important; }
.share-btn--sms:hover:not(:disabled) { background: #15803d !important; border-color: #15803d !important; }

.share-row--counter { justify-content: flex-end; padding-left: 72px; }
.char-counter { color: #6c757d; font-size: 0.75rem; }
.char-counter--warn { color: #d97706; font-weight: 600; }

.notify-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  background: #fff;
}
</style>
